import React, { useMemo } from "react";
import CountUp from "react-countup";
import BaseChart from "../../components/ui/BaseChart";

/* Total first, then the statuses. */
const CARDS = [
  { key: "totalExpense", label: "Total Expense", icon: "fa-solid fa-sack-dollar", color: "#4F46E5", bg: "#EEF2FF", primary: true },
  { key: "generated", label: "Generated", icon: "fa-solid fa-file-circle-plus", color: "#3B82F6", bg: "#EFF6FF" },
  { key: "verified", label: "Verified", icon: "fa-solid fa-clipboard-check", color: "#0891B2", bg: "#ECFEFF" },
  { key: "unpaid", label: "Unpaid", icon: "fa-solid fa-clock", color: "#F59E0B", bg: "#FFFBEB" },
  { key: "overdue", label: "Overdue", icon: "fa-solid fa-triangle-exclamation", color: "#EF4444", bg: "#FEF2F2" },
  { key: "paid", label: "Paid", icon: "fa-solid fa-circle-check", color: "#16A34A", bg: "#F0FDF4" },
];

/* ── Background area sparkline ─────────────────────────────────────────────── */
const Sparkline = ({ data, color }) => {
  const options = useMemo(
    () => ({
      chart: {
        type: "areaspline",
        height: 70,
        margin: [0, 0, 0, 0],
        backgroundColor: "transparent",
        animation: false,
      },
      title: { text: null },
      xAxis: { visible: false },
      yAxis: { visible: false, min: 0 },
      legend: { enabled: false },
      tooltip: { enabled: false },
      plotOptions: {
        areaspline: {
          marker: { enabled: false },
          lineWidth: 2.5,
          lineColor: color,
          fillColor: {
            linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
            stops: [
              [0, `${color}38`],
              [1, `${color}00`],
            ],
          },
          states: { hover: { enabled: false } },
        },
      },
      series: [{ name: "Trend", data: data?.length ? data : Array(6).fill(0) }],
    }),
    [data, color],
  );
  return <BaseChart options={options} style={{ width: "100%", height: "70px" }} />;
};

/* ── Month-over-month trend pill ───────────────────────────────────────────── */
const TrendBadge = ({ data }) => {
  if (!data || data.length < 2) return null;
  const last = data[data.length - 1];
  const prev = data[data.length - 2];
  if (prev === 0 && last === 0) return null;
  const pct = prev === 0 ? 100 : Math.round(((last - prev) / prev) * 100);
  const up = pct >= 0;
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-[3px] rounded-full ${
        up ? "bg-emerald-50 text-emerald-600" : "bg-rose-50 text-rose-500"
      }`}
    >
      <i className={`fa-solid ${up ? "fa-arrow-trend-up" : "fa-arrow-trend-down"} text-[9px]`} />
      {Math.abs(pct)}%
    </span>
  );
};

const ExpenseKpiCards = ({ kpis, mode = "expense" }) => {
  const total = kpis?.totalExpense?.amountUsd ?? 0;

  return (
    <div className="grid grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-4">
      {CARDS.map((card) => {
        const kpi = kpis?.[card.key] ?? { amountUsd: 0, count: 0, sparkline: [], extra: 0 };
        const label =
          card.primary && mode === "revenue" ? "Total Revenue" : card.label;
        const share =
          !card.primary && total > 0 ? Math.round((kpi.amountUsd / total) * 100) : null;
        // Company-flagged expenses, surfaced bottom-right on every card.
        const extra = kpi.extra ?? 0;

        return (
          <div
            key={card.key}
            className="group relative overflow-hidden rounded-2xl bg-white ring-1 ring-black/[0.06] shadow-[0_1px_2px_rgba(16,24,40,0.04),0_4px_16px_rgba(16,24,40,0.04)] flex flex-col min-h-[164px] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_8px_28px_rgba(16,24,40,0.10)]"
          >
            {/* top accent line — brightens on hover */}
            <span
              className="absolute inset-x-0 top-0 h-[3px] opacity-70 group-hover:opacity-100 transition-opacity"
              style={{ background: `linear-gradient(90deg, ${card.color}, ${card.color}00)` }}
            />

            {/* background sparkline */}
            <div className="absolute inset-x-0 bottom-0 h-[70px] opacity-80 pointer-events-none transition-opacity duration-300 group-hover:opacity-100">
              <Sparkline data={kpi.sparkline} color={card.color} />
            </div>
            {/* legibility fade behind the footer text */}
            <div className="absolute inset-x-0 bottom-0 h-[70px] bg-gradient-to-t from-white via-white/75 to-transparent pointer-events-none" />

            {/* content */}
            <div className="relative p-[18px] flex flex-col flex-1">
              <div className="flex items-start justify-between">
                <div
                  className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ring-1 transition-transform duration-300 group-hover:scale-105"
                  style={{ backgroundColor: card.bg, color: card.color, boxShadow: `0 0 0 1px ${card.color}1a inset` }}
                >
                  <i className={`${card.icon} text-[13px]`} />
                </div>
                <TrendBadge data={kpi.sparkline} />
              </div>

              <div className="mt-3.5">
                <p className="text-[10.5px] text-gray-400 font-semibold uppercase tracking-[0.08em] leading-none mb-2">
                  {label}
                </p>
                <p className="text-[1.45rem] font-bold text-gray-900 leading-none tracking-tight tabular-nums">
                  <CountUp
                    start={0}
                    end={kpi.amountUsd}
                    duration={1.1}
                    separator=","
                    decimals={2}
                    prefix="$"
                  />
                </p>
              </div>

              {/* footer — invoice count bottom-left, extra (company expenses) + share% bottom-right */}
              <div className="mt-auto flex items-center justify-between pt-3 text-[11px]">
                <span className="inline-flex items-center gap-1.5 font-semibold text-gray-500">
                  <i className="fa-light fa-file-invoice text-gray-300" />
                  <span className="tabular-nums">{kpi.count}</span> invoices
                </span>
                <span className="inline-flex items-center gap-2">
                  {/* Company expenses only apply to engineer (expense) invoices */}
                  {mode !== "revenue" && (
                    <span
                      className="font-semibold tabular-nums text-gray-400"
                      title="Company expenses"
                    >
                      Extra $
                      {extra.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </span>
                  )}
                  {share !== null && (
                    <span
                      className="font-semibold tabular-nums px-1.5 py-0.5 rounded-md"
                      style={{ color: card.color, backgroundColor: `${card.color}12` }}
                    >
                      {share}%
                    </span>
                  )}
                </span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ExpenseKpiCards;
